import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';

import { FormsModule, NgForm } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { MessageService } from 'primeng/api';
import { UserProfileDialogService } from 'app/core/services/user-profile-dialog.service';
import { AuthService } from 'app/core/services/auth.service';
import { Store } from '@ngrx/store';
import { updateUser, updateUserSuccess } from 'app/state/actions/user.actions';
import { UtilityService } from '@api/services/utility.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageCropperComponent, ImageCroppedEvent } from 'ngx-image-cropper';
import { HttpClient, HttpContext } from '@angular/common/http';
import { setLoading } from 'app/state/actions/loading.actions';
import { SKIP_WITH_CREDENTIALS_HTTP } from 'app/core/interceptors/skip-with-credentials-http.token';
import { last, switchMap, tap, catchError } from 'rxjs/operators';
import { EMPTY, throwError, Subscription } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'app-user-profile-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ButtonModule,
    DialogModule,
    FloatLabelModule,
    InputTextModule,
    DividerModule,
    SelectButtonModule,
    FileUploadModule,
    ImageModule,
    ImageCropperComponent,
    SkeletonModule
],
  templateUrl: './user-profile-dialog.component.html',
})
export class UserProfileDialogComponent {
  private readonly defaultAvatarUrl = 'assets/images/default-avatar.svg';
  visible = false;
  displayName = '';
  email = '';
  avatarUrl = '';
  shareEmail = false;
  private userId: string = '';
  loading = true;
  // Avatar upload/crop state
  showAvatarUploadModal = false;
  imageFile: File | undefined;
  croppedImage: SafeUrl = '';
  croppedBlob: Blob | null = null;
  private subscriptions = new Subscription();
  private avatarUploadSubscription?: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly dialogService: UserProfileDialogService,
    private readonly authService: AuthService,
    private readonly messageService: MessageService,
    private readonly store: Store,
    private readonly utilityService: UtilityService,
    private readonly sanitizer: DomSanitizer,
    private readonly http: HttpClient,
    private readonly actions$: Actions
  ) {
    this.subscriptions.add(this.dialogService.visible$.subscribe(v => {
      this.visible = v;
      this.cdr.markForCheck();
    }));
    this.subscriptions.add(this.authService.userData$.subscribe((userData) => {
      if (!userData || (this.userId && userData.id !== this.userId)) {
        this.cancelAvatarUpload();
        this.userId = '';
        this.displayName = this.email = this.avatarUrl = '';
        this.shareEmail = false;
        this.visible = this.showAvatarUploadModal = false;
        this.croppedImage = '';
        this.croppedBlob = null;
        this.imageFile = undefined;
        this.loading = false;
        this.cdr.markForCheck();
      }
      if (userData) {
        this.avatarUrl = userData.picture || this.defaultAvatarUrl;
        this.displayName = userData.fullName || '';
        this.email = userData.email || '';
        this.shareEmail = userData.privacy?.shareEmail ?? false;
        this.userId = userData.id!;
        if (userData.fullName) {
          this.loading = false;
          this.cdr.markForCheck();
        }
      }
    }));
    this.subscriptions.add(
      this.actions$.pipe(
        ofType(updateUserSuccess),
        tap(({ userId }) => {
          if (userId !== this.userId) return;
          this.messageService.add({
            severity: 'success',
            summary: 'Profile updated',
            detail: 'Your profile has been updated successfully.'
          });
          this.closeDialog();
          this.cdr.markForCheck();
        })
      ).subscribe()
    );
  }

  openDialog() {
    this.visible = true;
  }

  closeDialog() {
    this.visible = false;
  }

  // Avatar upload logic
  showAvatarModal() {
    this.showAvatarUploadModal = true;
  }

  onAvatarSelected(event: any) {
    if (event?.originalEvent?.target?.files?.length > 0) {
      this.imageFile = event.originalEvent.target.files[0];
      this.showAvatarUploadModal = true;
    }
  }

  imageCropped(evt: ImageCroppedEvent) {
    this.croppedImage = this.sanitizer.bypassSecurityTrustUrl(evt.objectUrl!);
    this.croppedBlob = evt.blob ?? null;
  }

  imageLoaded() {}
  cropperReady() {}
  loadImageFailed() {}

  confirmAvatarCrop() {
    if (!this.croppedBlob || !this.userId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not process avatar image.'
      });
      return;
    }
    this.cancelAvatarUpload();
    this.store.dispatch(setLoading(true));
    const file = new File([this.croppedBlob], 'avatar.webp', { type: 'image/webp' });
    const body = {
      contentType: 'image/webp' as const,
    };
    this.avatarUploadSubscription = this.utilityService.utilityGenerateUploadUrl({ body }).pipe(
      tap(response => {}),
      switchMap(response => {
        const uploadIntentId = response.uploadIntentId as string;
        const uploadUrl = response.uploadUrl as string;
        const downloadUrl = response.downloadUrl as string;
        return this.http.put(uploadUrl, file, {
          headers: {
            'Content-Type': 'image/webp',
            'Access-Control-Allow-Origin': '*',
            'Cache-Control': 'public, max-age=2592000, immutable',
          },
          reportProgress: true,
          observe: 'events',
          context: new HttpContext().set(SKIP_WITH_CREDENTIALS_HTTP, true)
        }).pipe(
          last(),
          tap(() => {
            this.updateUserAvatar(downloadUrl);
            this.showAvatarUploadModal = false;
            this.store.dispatch(setLoading(false));
          }),
          catchError(error => this.handleUploadError(error))
        );
      }),
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Upload intent failed',
          detail: 'Could not create upload intent.'
        });
        this.store.dispatch(setLoading(false));
        return EMPTY;
      })
    ).subscribe();
  }

  private updateUserAvatar(downloadUrl: string) {
    this.store.dispatch(updateUser({
      userId: this.userId,
      changes: { picture: downloadUrl }
    }));
    this.messageService.add({
      severity: 'success',
      summary: 'Avatar updated',
      detail: 'Your avatar has been updated.'
    });
    // Optionally update avatarUrl locally
    // this.avatarUrl = ... (fetch new avatar if needed)
  }

  private handleUploadError(error: any) {
    this.messageService.add({
      severity: 'error',
      summary: 'Upload failed',
      detail: 'Failed to upload avatar. Please try again.'
    });
    this.store.dispatch(setLoading(false));
    return throwError(() => error);
  }

  saveChanges(form?: NgForm) {
    if (form && form.invalid) {
      if (form.controls['email']?.invalid) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Please enter a valid email address.'
        });
      } else if (form.controls['displayName']?.invalid) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'User Name is required and must be at least 3 characters long.'
        });
      }
      return;
    }
    // Only send changed fields
    const changes: Record<string, unknown> = {};
    if (this.displayName !== this.authService.getUserName()) {
      changes['fullName'] = this.displayName;
    }
    if (this.email !== this.authService.getUserEmail()) {
      changes['email'] = this.email;
    }
    if (this.shareEmail !== this.authService.getUserShareEmail()) {
      changes['privacy'] = { shareEmail: this.shareEmail };
    }
    if (Object.keys(changes).length > 0 && this.userId) {
      this.store.dispatch(updateUser({ userId: this.userId, changes }));
    }
  }

  onHide(): void {
    this.dialogService.close();
  }

  ngOnDestroy(): void {
    this.cancelAvatarUpload();
    this.subscriptions.unsubscribe();
  }

  private cancelAvatarUpload(): void {
    if (this.avatarUploadSubscription && !this.avatarUploadSubscription.closed) {
      this.avatarUploadSubscription.unsubscribe();
      this.store.dispatch(setLoading(false));
    }
  }
}
